import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Button, Image } from 'react-bootstrap';
import { IconPaymentMethodMastercard, IconPaymentMethodVisa } from '@pegasus/workforce-icons';
import { useRecoilValue } from 'recoil';

import { useTitle } from 'hooks/UseTitle';
import { createPendingCart, ICreatePendingCart } from 'modules/create-pending-cart';
import { ICartItems, IPendingCart } from 'models/cart/IPendingCart';
import { LoadingSpinner } from 'components/shared/LoadingSpinner/LoadingSpinner';
import { SubtotalTable } from 'components/shared/SubtotalTable/SubtotalTable';
import { ErrorModal } from 'components/modals/ErrorModal/ErrorModal';
import { FatZebraForm } from 'components/Payment/FatZebraForm';
import { FullScreenModal } from 'components/shared/FullScreenModal/FullScreenModal';
import { FullScreenModalTitle } from 'components/shared/FullScreenModal/FullScreenModalTitle';
import { individualState } from 'state/individual/individual-state';

import './style.scss';

interface ICreditCardPayment {
    handlePaymentSuccess: () => void;
}

export interface ICartTotals {
    subtotal: number;
    taxRate: number;
    total: number;
}

export const CreditCardPayment = ({handlePaymentSuccess}: ICreditCardPayment) => {
    const {t} = useTranslation();
    useTitle(t('payments.creditCardPurchase', 'Credit Card Payment'));

    const newWorker = useRecoilValue(individualState);
    const [pendingCart, setPendingCart] = React.useState<IPendingCart | null>();

    const [token, setToken] = React.useState<string>('');
    const [completeToken, setCompleteToken] = React.useState<string>('');
    const [queryString, setQueryString] = React.useState<ICreatePendingCart>();
    const [errorModal, setErrorModal] = React.useState<boolean | string | IPendingCart>(false);
    const [paymentState, setPaymentState] = React.useState<boolean>(false);
    const [cartTotals, setCartTotals] = React.useState<ICartTotals>({
        subtotal: 0,
        taxRate: 0,
        total: 0
    });

    /**
     * Creates pending cart by calling API, displays error modal if needed.
     * If error caused by recaptcha invalid, gets new recaptcha
     *
     * @param purchaseIdent url params, portal, company, employee
     */
    const getCart = async (purchaseIdent: ICreatePendingCart) => {
        if (!newWorker?.onsiteToken) {
            setErrorModal('User Profile Not Found.');
            return;
        }
        const pendingCart: IPendingCart = await createPendingCart(purchaseIdent);
        if ('errorCode' in pendingCart && pendingCart.message) {
            if (pendingCart.errorCode === 'ADD_PENDING_CARD_TO_CART' || pendingCart.errorCode === 'REQUEST_FAILED') {
                setErrorModal('No outstanding payments for this account.');
            } else if (pendingCart.errorCode === 'START_CHECKOUT') {
                setErrorModal(pendingCart.message);
            } else {
                setErrorModal(pendingCart.message);
                loadRecaptcha();
            }
        } else {
            setQueryString(purchaseIdent);
            setPendingCart(pendingCart);
        }
    };

    /**
     * Closes the error modal
     */
    const handleCloseError = () => {
        setErrorModal(false);
    };

    /**
     * sets recaptcha token when loaded
     */
    const handleLoaded = () => {
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(recaptchaId, {action: 'register'})
                .then(newToken => {
                    if (!token) {
                        const queryString = window.location.search;
                        const urlParams = new URLSearchParams(queryString);

                        const portalId = urlParams.get('portal');
                        const companyId = urlParams.get('company');
                        const employeeId = urlParams.get('employee');

                        if (portalId && companyId && employeeId) {
                            getCart({
                                portalId,
                                companyId,
                                employeeId,
                                onsiteToken: newWorker?.onsiteToken,
                                token: newToken
                            });
                        }
                    }
                    setToken(newToken);
                });
        });
    };

    /**
     * sets recaptcha token when loaded
     */
    const handleLoaded2 = () => {
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(recaptchaId, {action: 'register'})
                .then(token => {
                    setCompleteToken(token);
                });
        });
    };

    /**
     * Adds two recaptcha tokens to the page
     * TODO: recaptcha code duplicated here and CreateWorker.tsx, clean up
     */
    React.useEffect(() => {
        // Add reCaptcha
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaId}`;
        script.addEventListener('load', handleLoaded);
        document.body.appendChild(script);
        const completeScript = document.createElement('script');
        completeScript.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaId}`;
        completeScript.addEventListener('load', handleLoaded2);
        document.body.appendChild(completeScript);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * reloads recaptcha token. called on user interaction with form elements
     */
    const loadRecaptcha = () => {
        handleLoaded();
        handleLoaded2();
    };

    /**
     * Calculated price totals from pendingCart
     */
    React.useEffect(() => {
        let subtotal: number = 0;
        let tax: number = 0;
        pendingCart?.cart?.items.forEach((item: ICartItems) => {
            subtotal = item.itemCost + subtotal;
            tax = item.itemGST + tax;
        });
        setCartTotals({
            subtotal: subtotal,
            taxRate: tax,
            total: subtotal + tax
        });
    }, [pendingCart]);

    return (
        <FullScreenModal className="CreditPayment" close={false}>
            <ErrorModal handleClose={handleCloseError} show={!!errorModal} error={errorModal}/>
            <FullScreenModalTitle>
                {t('payments.pendingTitle', 'Payment Required')}
            </FullScreenModalTitle>
            <div className="text-center mt-5">
                <Trans i18nKey="payments.validSubscriptionPaymentRequired">
                    A payment of your first year subscription fee is required
                    before your profile can be created
                </Trans>
            </div>
            <div className="text-center font-weight-bold my-3">
                {/*TODO: make this price dynamic*/}
                {t('payments.subscription fee', 'Subscription Fee: $77 per year.')}
            </div>
            <div className="text-center w-75 mx-auto mb-4">
                <Trans i18nKey="payments.descriptionProceedPayment">
                    Please make a payment below in order to complete the registration process. If you decide to cancel,
                    your profile will not be created.
                </Trans>
            </div>
            {!pendingCart ? (
                <LoadingSpinner/>
            ) : (
                <>
                    <div className="form-label--title">
                        {t('payment.totalPaymentAmount', 'Total Payment Amount')}
                    </div>
                    <div className="CreditPayment__subtitle-text mb-3">
                        {t('payment.reviewAmountToBeCharged', 'Please review the total amount that will be charged in this payment.')}
                    </div>
                    <SubtotalTable cartTotals={cartTotals} pendingCart={pendingCart}/>
                    <div className="form-label--title">
                        {t('payment.acceptedCards', 'Accepted Card Types')}
                    </div>
                    <div className="CreditPayment__subtitle-text">
                        {t('payment.onlyCardsAccepted', 'Only VISA and MasterCard are accepted at this time.')}
                    </div>
                    <div className="d-flex flex-row justify-content-start">
                        <div className="CreditPayment__logos-div">
                            <IconPaymentMethodVisa className="wficon-xl"/>
                        </div>
                        <div className="CreditPayment__logos-div">
                            <IconPaymentMethodMastercard className="wficon-xl"/>
                        </div>
                    </div>
                    {/* smoothly hides this when transaction is being processed */}
                    <div className="CreditPayment__cardDetails" style={{height: paymentState ? 0 : '2.5rem'}}>
                        <div className="form-label--title">
                            {t('payment.cardDetails', 'Card Details')}
                        </div>
                        <div className="CreditPayment__subtitle-text mb-3">
                            {t('payment.pleaseCompleteFields', 'Please complete each of the fields below in order to make a payment.')}
                        </div>
                    </div>

                    <div className="text-center">
                        <FatZebraForm
                            handlePaymentSuccess={handlePaymentSuccess}
                            pendingCart={pendingCart}
                            queryString={queryString}
                            completeToken={completeToken}
                            loadRecaptcha={loadRecaptcha}
                            setPaymentState={setPaymentState}
                            paymentState={paymentState}
                        />
                    </div>
                </>
            )}
            <Button
                href={'/'} // back to IWR home page
                type="button"
                variant="default"
                className="btn-xl mt-2"
                block
            >
                {t('common.cancel', 'Cancel')}
            </Button>
            <div className="CreateWorkerSuccess__logoDiv mt-5">
                <Image src="/assets/images/DSP-Logo.svg" className="CreateWorkerSuccess__logo"/>
            </div>
            <div className="CreateWorkerSuccess__needHelp text-center">
                {t('CreateWorkerSuccess.needHelp', 'Need Help? Call Support on 1300 192 365')}
            </div>
        </FullScreenModal>
    );
};
